"use client";

import dynamic from "next/dynamic";
import { useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";

import {
  CAREER_CHART_CHECK_FIELD_IDS,
  CAREER_CHART_CHECK_FORM,
  CAREER_CHART_CHECK_FRAME,
  CAREER_CHART_FORM_RESERVE,
} from "./career-chart-check-form";

// The /career-horoscope/ check's boundary (the rashi check's pattern,
// RashiCheckLazy): the server page imports only this module, and it owns the
// one dynamic() to the island. The form, place search, engine client and answer
// load as an async chunk; the first load gains only this file and the shell.
//
// Activation is element-scoped and local: no window listener and no timer, so a
// reader scrolling the guide never downloads the island; a press, focus or
// submit on the shell does.
//
// The slot reserves the form's tallest state, so neither shell -> form nor an
// engine error after a slow reply moves the guide below; the answer's taller
// room is added by the island in the press that sends the request
// (CAREER_CHART_ANSWER_RESERVE). Natural heights measured 2026-10-18 (next dev +
// Playwright, 7 widths, service workers blocked, the engine mocked with four
// answers, an unreadable reply and a failure, the check in the page's hero): the
// empty form is 414 at 320-414 px wide, 398 at 480-639 and 356 from 640; with
// the field errors of an empty submit it is 492 at 320-360, 476 at 390-414, 460
// at 480-639 and 414 from 640; with the engine-failure or unreadable alert it is
// at most 476 on phones and 400 from 640. Hence 520 px on phones and 460 px from
// the sm breakpoint (CAREER_CHART_FORM_RESERVE), the love check's floors.
// translate="no" covers the shell, form and answer, which re-render on "check
// another" (tests/translate-safe-islands.test.ts).
const SHELL_PROPS = { ...CAREER_CHART_CHECK_FORM, className: CAREER_CHART_CHECK_FRAME } as const;

const CareerChartCheck = dynamic(() => import("./CareerChartCheck"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL_PROPS} />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: CAREER_CHART_CHECK_FIELD_IDS.date,
  tob: CAREER_CHART_CHECK_FIELD_IDS.time,
  place: CAREER_CHART_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || CAREER_CHART_CHECK_FIELD_IDS.date;
}

export default function CareerChartCheckLazy() {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // Focus follows the visitor into the real form after a keyboard focus, a
  // mouse press or a submit, and never after a touch, which may start a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(CAREER_CHART_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-career-chart-check-slot=""
      className={`flex flex-col ${CAREER_CHART_FORM_RESERVE}`}
      {...shellIntent}
    >
      {active ? (
        <CareerChartCheck focusFieldId={focusFieldId} />
      ) : (
        <BirthToolFormShell {...SHELL_PROPS} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
